import { useEffect, useState } from "react";
import DashboardShell, { StatCard } from "@/components/DashboardShell";
import { api, fmtNGN, formatErr } from "@/lib/api";
import MonoIdentityVerify from "@/components/MonoIdentityVerify";
import MonoConnectButton from "@/components/MonoConnectButton";
import { BackArrow, ReviewPanel } from "@/components/StepNav";
import RateExplanation from "@/components/RateExplanation";
import { toast } from "sonner";
import {
  Phone, IdentificationCard, Bank, Sliders, ShieldCheck, Lightning, CheckCircle, XCircle,
  CircleNotch, GraduationCap, EnvelopeSimple, ArrowClockwise,
} from "@phosphor-icons/react";

const EDITABLE = ["none", "kyc_pending", "email_verification_pending", "bank_linking", "offer_available"];
const STATES = ["none", "otp_pending", "kyc_pending", "email_verification_pending", "bank_linking", "offer_available", "mandate_pending", "payout_pending", "disbursed", "repaid", "declined"];

function ModulePayoutPending() {
  return (
    <SectionCard icon={<Lightning size={18} weight="fill" />} title="Mandate active · payout in progress">
      <p className="text-sm text-[#4F5D4F]" data-testid="ala-payout-pending">Your salary mandate is active. Our team is sending your advance to your salary account by bank transfer. You'll get an email as soon as it's on its way.</p>
    </SectionCard>
  );
}

export default function ParentAcademicAdvance() {
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [institutions, setInstitutions] = useState([]);
  const [coops, setCoops] = useState([]);
  const [blockedDomains, setBlockedDomains] = useState([]);

  const reload = async () => {
    setLoading(true);
    try {
      const [{ data: appData }, { data: lists }] = await Promise.all([
        api.get("/ala/status"),
        api.get("/ala/institutions"),
      ]);
      setApp(appData);
      setInstitutions(lists.institutions || []);
      setCoops(lists.cooperatives || []);
      setBlockedDomains(lists.blocked_email_domains || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const [viewIdx, setViewIdx] = useState(null);
  const [editing, setEditing] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const stage = app?.state || "none";
  const declinedAt = app?.accepted_terms ? 6 : app?.bank_account ? 5 : app?.kyc ? 4 : 2;
  const curIdx = stage === "none" ? (otpSent ? 1 : 0) : stage === "declined" ? declinedAt : Math.max(0, STATES.indexOf(stage));
  const shownIdx = viewIdx ?? curIdx;
  const locked = !EDITABLE.includes(stage);

  const refresh = async () => { await reload(); setViewIdx(null); setEditing(false); };
  const back = () => {
    setEditing(false);
    if (viewIdx === null && stage === "none") return setOtpSent(false);
    setViewIdx(shownIdx - 1);
  };
  const next = () => { setEditing(false); setViewIdx(shownIdx + 1 >= curIdx ? null : shownIdx + 1); };
  const goEdit = (idx) => { setViewIdx(idx); setEditing(true); };

  return (
    <DashboardShell
      title="Academic Liquidity Advance"
      subtitle="Short-tenor payroll-backed micro-loans for Nigerian academic staff."
    >
      <div className="mb-6" data-testid="ala-step-rail">
        <div className="flex items-center justify-between text-xs text-[#4F5D4F] mb-2">
          <span className="font-semibold uppercase tracking-[0.14em]" data-testid="ala-step-label">Step {shownIdx + 1} of {STATES.length}</span>
          <span className="font-mono">{Math.round(((shownIdx + 1) / STATES.length) * 100)}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-[#E5E3DB] overflow-hidden">
          <div className="h-full rounded-full bg-[#0A3622] transition-all" style={{ width: `${((shownIdx + 1) / STATES.length) * 100}%` }} />
        </div>
      </div>

      {!loading && shownIdx > 0 && <BackArrow onClick={back} testid="ala-back-arrow" />}

      {loading ? (
        <div className="card text-center py-10"><CircleNotch size={28} className="animate-spin mx-auto text-[#0A3622]" /></div>
      ) : viewIdx !== null ? (
        <AlaReview idx={viewIdx} app={app} locked={locked} editing={editing} setEditing={setEditing} goEdit={goEdit}
          next={next} refresh={refresh} institutions={institutions} coops={coops} blockedDomains={blockedDomains} />
      ) : (
        <>
          {stage === "none" || stage === "otp_pending"
            ? <Module1Phone app={app} reload={refresh} otpSent={otpSent} setOtpSent={setOtpSent} />
            : stage === "kyc_pending"
            ? <Module1KYC institutions={institutions} coops={coops} blockedDomains={blockedDomains} reload={refresh} />
            : stage === "email_verification_pending"
            ? <Module1EmailVerify app={app} reload={refresh} />
            : stage === "bank_linking"
            ? <Module2Bank reload={refresh} />
            : stage === "offer_available"
            ? <Module3Loan app={app} reload={refresh} />
            : stage === "mandate_pending"
            ? <Module4Mandate app={app} reload={refresh} />
            : stage === "payout_pending"
            ? <ModulePayoutPending />
            : stage === "disbursed" || stage === "repaid"
            ? <ModuleDisbursed app={app} />
            : stage === "declined"
            ? <ModuleDeclined app={app} reload={refresh} />
            : null}
        </>
      )}
    </DashboardShell>
  );
}

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString() : null);

/* ───────────── Read-only review of completed steps ───────────── */
function AlaReview({ idx, app, locked, editing, setEditing, goEdit, next, refresh, institutions, coops, blockedDomains }) {
  const [otpSent, setOtpSent] = useState(false);
  const kyc = app.kyc || {};
  const bank = app.bank_account || {};
  const terms = app.accepted_terms || {};
  const editBtn = (label, onClick, testid) => !locked && (
    <button type="button" onClick={onClick} className="btn-outline text-sm" data-testid={testid}>{label}</button>
  );
  const phoneEdit = editing
    ? <Module1Phone app={app} reload={refresh} otpSent={otpSent} setOtpSent={setOtpSent} />
    : editBtn("Change number", () => setEditing(true), "ala-review-change-phone");

  if (editing && idx === 2) return <Module1KYC prior={kyc} institutions={institutions} coops={coops} blockedDomains={blockedDomains} reload={refresh} onUnchanged={next} />;

  const panels = {
    0: { title: "Phone number", rows: [["Phone", app.phone]], extra: phoneEdit },
    1: { title: "Phone code", rows: [["Phone", app.phone], ["Verified", fmtWhen(app.phone_verified_at)]], extra: phoneEdit },
    2: { title: "KYC & staff verification", rows: [["Name", kyc.full_name], ["Institution", kyc.institution], ["Staff ID", kyc.staff_id], ["Official email", kyc.official_email], ["BVN", kyc.bvn_last4 && `****${kyc.bvn_last4}`], ["NIN", kyc.nin_last4 && `****${kyc.nin_last4}`], ["Cooperative", kyc.cooperative_member ? kyc.cooperative_chapter : "Not a member"]],
      extra: editBtn("Edit details", () => setEditing(true), "ala-review-edit-kyc") },
    3: { title: "Institution email", rows: [["Email", kyc.official_email], ["Verified", fmtWhen(kyc.official_email_verified_at)]],
      extra: editBtn("Change email", () => goEdit(2), "ala-review-change-email") },
    4: { title: "Salary account", rows: [["Bank", bank.bank_name], ["Account", bank.account_number && `****${String(bank.account_number).slice(-4)}`], ["Account name", bank.account_name]],
      extra: !locked && <MonoConnectButton label="Link a different account" testid="ala-review-relink-bank" onLinked={(acc) => linkBank(acc, refresh)} /> },
    5: { title: "Loan offer", rows: [["Principal", fmtNGN(terms.principal)], ["Tenor", terms.tenor_days && `${terms.tenor_days} days`], ["Instalment", fmtNGN(terms.monthly_installment)], ["Total repayment", fmtNGN(terms.total_repayment)], ["Signed by", app.e_signature?.name]] },
    6: { title: "Remita mandate", rows: [["Mandate reference", app.mandate_reference], ["Activated", fmtWhen(app.mandate_active_at)]] },
    7: { title: "Payout", rows: [["Status", app.disbursement?.status || "Sent"]] },
    8: { title: "Loan disbursed", rows: [["NIP reference", app.disbursement?.nip_reference], ["Status", app.disbursement?.status]] },
    9: { title: "Loan repaid", rows: [["Status", "Repaid"]] },
  };
  const p = panels[idx] || { title: "Completed step", rows: [] };
  return (
    <ReviewPanel title={p.title} rows={p.rows} locked={locked} closed={app.state === "declined"} onNext={next} testid={`ala-review-step-${idx + 1}`}>
      {p.extra}
    </ReviewPanel>
  );
}

/* ───────────── Module 1a — Phone + OTP ───────────── */
function Module1Phone({ app, reload, otpSent, setOtpSent }) {
  const [phone, setPhone] = useState(app?.phone || "+234");
  const phoneOk = /^\+?\d{11,13}$/.test(phone.trim());
  const [sentFor, setSentFor] = useState("");
  const [mockCode, setMockCode] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [remaining, setRemaining] = useState(null);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const [channel, setChannel] = useState("sms");
  const [sentTo, setSentTo] = useState("");
  const send = async (ch = channel) => {
    setBusy(true);
    setChannel(ch);
    try {
      const { data } = await api.post("/ala/otp/start", { phone, channel: ch });
      setSentTo(data.sent_to || "");
      setOtpSent(true);
      setSentFor(phone);
      setRemaining(data.sends_remaining);
      setCooldown(data.resend_after_seconds || 0);
      if (data.mock_code) {
        setMockCode(data.mock_code);
        toast.info(`Mock ${ch === "email" ? "email" : "SMS"} — your code is ${data.mock_code}`);
      } else toast.success(ch === "email" ? `Code emailed to ${data.sent_to}` : "SMS sent — check your phone");
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      const m = /wait (\d+) seconds/.exec(detail);
      if (m) setCooldown(Number(m[1]));
      if (/limit of \d+ codes/.test(detail)) setRemaining(0);
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
    finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try {
      await api.post("/ala/otp/verify", { phone, code });
      toast.success("Phone verified");
      reload();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <SectionCard icon={<Phone size={18} weight="fill" />} title="Module 1 · Verify your phone number">
      <p className="text-sm text-[#4F5D4F] mb-5">We'll send you a 6-digit code to confirm it's you, by SMS or to the email you registered with.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Phone (E.164)">
          <input className="input mt-1 font-mono" value={phone} disabled={otpSent} onChange={(e) => setPhone(e.target.value)} placeholder="+2348012345678" data-testid="ala-phone-input" />
        </Field>
        {!otpSent && sentFor && sentFor === phone ? (
          <div className="flex items-end">
            <button onClick={() => setOtpSent(true)} className="btn-primary w-full justify-center" data-testid="ala-continue-to-code">
              Next — enter the code we sent
            </button>
          </div>
        ) : !otpSent ? (
          <div className="flex flex-col justify-end gap-2" data-testid="ala-otp-channel-choice">
            {remaining === 0 || cooldown > 0 ? (
              <button disabled className="btn-primary w-full justify-center disabled:opacity-50" data-testid="ala-send-otp">
                {remaining === 0 ? "Daily code limit reached" : `Try again in ${cooldown}s`}
              </button>
            ) : (
              <>
                <button onClick={() => send("sms")} disabled={busy || !phoneOk} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="ala-send-otp">
                  {busy && channel === "sms" ? <CircleNotch className="animate-spin" size={16} /> : <><Phone size={16} weight="fill" /> Send to my phone (SMS)</>}
                </button>
                <button onClick={() => send("email")} disabled={busy || !phoneOk} className="btn-outline w-full justify-center disabled:opacity-50" data-testid="ala-send-otp-email">
                  {busy && channel === "email" ? <CircleNotch className="animate-spin" size={16} /> : <><EnvelopeSimple size={16} weight="fill" /> Send to my email</>}
                </button>
              </>
            )}
          </div>
        ) : (
          <>
            <Field label="6-digit code">
              <input className="input mt-1 font-mono tracking-widest text-lg" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="123456" data-testid="ala-code-input" />
              {sentTo && <p className="text-xs text-[#4F5D4F] mt-1" data-testid="ala-otp-sent-to">Code sent {channel === "email" ? "by email" : "by SMS"} to {sentTo}</p>}
              {mockCode && <p className="text-xs text-[#4F5D4F] mt-1">Mock mode — use <span className="font-mono text-[#0A3622]">{mockCode}</span></p>}
              <div className="mt-2 flex items-center gap-3 text-xs" data-testid="ala-otp-resend-row">
                <button type="button" onClick={() => send(channel)} disabled={busy || cooldown > 0 || remaining === 0}
                  className="font-semibold text-[#0A3622] underline disabled:no-underline disabled:text-[#4F5D4F]/70" data-testid="ala-resend-otp">
                  {remaining === 0 ? "Daily code limit reached" : cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
                </button>
                {remaining !== null && remaining > 0 && <span className="text-[#4F5D4F]" data-testid="ala-otp-remaining">{remaining} left today</span>}
                <button type="button" onClick={() => { setOtpSent(false); setCode(""); setMockCode(""); }} className="text-[#0A3622] underline ml-auto" data-testid="ala-change-phone">Change number or method</button>
              </div>
            </Field>
            <div className="flex items-end sm:col-start-2">
              <button onClick={verify} disabled={busy || code.length !== 6} className="btn-primary w-full justify-center" data-testid="ala-verify-otp">
                {busy ? <CircleNotch className="animate-spin" size={16} /> : <>Verify <CheckCircle size={16} weight="fill" /></>}
              </button>
            </div>
          </>
        )}
      </div>
    </SectionCard>
  );
}

/* ───────────── Module 1b — KYC ───────────── */
function kycInitial(prior, institutions) {
  if (!prior) return { bvn: "", nin: "", institution: "", institution_other: "", staff_id: "", official_email: "", cooperative_member: false, cooperative_chapter: "" };
  const custom = prior.institution_custom || !institutions.includes(prior.institution);
  return {
    bvn: "", nin: "", institution: custom ? "Others" : prior.institution, institution_other: custom ? prior.institution : "",
    staff_id: prior.staff_id || "", official_email: prior.official_email || "",
    cooperative_member: !!prior.cooperative_member, cooperative_chapter: prior.cooperative_chapter || "",
  };
}

function Module1KYC({ institutions, coops, blockedDomains, reload, prior, onUnchanged }) {
  const initial = useState(() => kycInitial(prior, institutions))[0];
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [score, setScore] = useState(null);
  const [ident, setIdent] = useState(prior ? { reused: true } : null);
  const isOthers = f.institution === "Others";

  const emailDomain = f.official_email.includes("@") ? f.official_email.split("@").pop().toLowerCase().trim() : "";
  const emailIsPersonal = emailDomain && (blockedDomains || []).includes(emailDomain);
  const complete = !!ident && f.institution && (!isOthers || f.institution_other.trim().length >= 3) && f.staff_id.trim()
    && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.official_email.trim()) && (!f.cooperative_member || f.cooperative_chapter);
  const submit = async (e) => {
    e.preventDefault();
    if (emailIsPersonal) {
      toast.error(`'${emailDomain}' is a personal email provider — use your official institution email.`);
      return;
    }
    if (!ident) return toast.error("Verify your BVN and NIN first");
    if (prior && ident.reused && JSON.stringify(f) === JSON.stringify(initial)) {
      toast.info("No changes — your details stay verified");
      return onUnchanged?.();
    }
    if (prior && !window.confirm("Saving these changes means you'll need to verify your email and link your salary account again. Continue?")) return;
    setBusy(true);
    try {
      const { bvn, nin, ...rest } = f;
      const payload = { ...rest, app_base_url: window.location.origin, ...(ident.reused ? { reuse_verified_identity: true } : { bvn, nin }) };
      if (!isOthers) payload.institution_other = null;
      const { data } = await api.post("/ala/kyc", payload);
      if (!data.ok) {
        setScore(data.confidence_score);
        toast.error(`Match confidence ${data.confidence_score}% — need >90%. ${data.reason}`);
      } else {
        toast.success(`KYC verified · match confidence ${data.confidence_score}%`);
        reload();
      }
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit}>
      <SectionCard icon={<IdentificationCard size={18} weight="fill" />} title="Module 1 · KYC & staff verification">
        {score !== null && score < 90 && (
          <div className="p-3 rounded-xl bg-[#FBE3E3] text-[#C93B3B] text-sm mb-5">
            Your BVN and NIN details match {score}%. We need at least 90%. Please check you entered your own BVN and NIN.
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <MonoIdentityVerify prior={prior} onVerified={(v) => { setIdent(v); setF((x) => ({ ...x, bvn: v.bvn || "", nin: v.nin || "" })); }} onReset={() => setIdent(null)} />
          </div>
          <Field label="Institution">
            <select className="input mt-1" required value={f.institution} onChange={(e) => setF({ ...f, institution: e.target.value, institution_other: "" })} data-testid="ala-institution-select">
              <option value="">Select your institution…</option>
              {institutions.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </Field>
          {isOthers && (
            <Field label="Type your institution name">
              <input
                className="input mt-1"
                required
                minLength={3}
                maxLength={120}
                value={f.institution_other}
                onChange={(e) => setF({ ...f, institution_other: e.target.value })}
                placeholder="e.g. Pan-Atlantic University"
                data-testid="ala-institution-other-input"
              />
            </Field>
          )}
          <Field label="Staff ID number"><input className="input mt-1 font-mono" required value={f.staff_id} onChange={(e) => setF({ ...f, staff_id: e.target.value })} data-testid="ala-staff-id-input" /></Field>
          <Field label="Official institution email">
            <input
              type="email"
              className={`input mt-1 ${emailIsPersonal ? "border-[#C93B3B] focus:border-[#C93B3B]" : ""}`}
              required
              value={f.official_email}
              onChange={(e) => setF({ ...f, official_email: e.target.value })}
              placeholder="firstname.lastname@unilag.edu.ng"
              data-testid="ala-official-email-input"
            />
            {emailIsPersonal ? (
              <p className="text-xs text-[#C93B3B] mt-1" data-testid="ala-email-personal-warning">
                <strong>{emailDomain}</strong> is a personal email provider. Please use your official institution email (e.g. <span className="font-mono">yourname@unilag.edu.ng</span>).
              </p>
            ) : (
              <p className="text-xs text-[#4F5D4F] mt-1">We'll send a verification link here. You'll need to click it from your inbox before we continue.</p>
            )}
          </Field>
          <Field label="Cooperative member?">
            <div className="flex gap-2 mt-1">
              <label className={`flex-1 text-center py-2.5 rounded-full border cursor-pointer text-sm ${f.cooperative_member ? "border-[#0A3622] bg-[#E2F3EB]" : "border-[#E5E3DB]"}`}>
                <input type="radio" className="sr-only" checked={f.cooperative_member} onChange={() => setF({ ...f, cooperative_member: true })} />Yes
              </label>
              <label className={`flex-1 text-center py-2.5 rounded-full border cursor-pointer text-sm ${!f.cooperative_member ? "border-[#0A3622] bg-[#E2F3EB]" : "border-[#E5E3DB]"}`}>
                <input type="radio" className="sr-only" checked={!f.cooperative_member} onChange={() => setF({ ...f, cooperative_member: false, cooperative_chapter: "" })} />No
              </label>
            </div>
          </Field>
          {f.cooperative_member && (
            <Field label="Cooperative chapter">
              <select className="input mt-1" required value={f.cooperative_chapter} onChange={(e) => setF({ ...f, cooperative_chapter: e.target.value })} data-testid="ala-cooperative-select">
                <option value="">Select chapter…</option>
                {coops.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          )}
        </div>
        <button type="submit" disabled={busy || emailIsPersonal || !complete} className="btn-primary mt-6 disabled:opacity-50" data-testid="ala-kyc-submit">
          {busy ? <CircleNotch className="animate-spin" size={16} /> : <>{prior ? "Save changes" : "Verify identity"} <ShieldCheck size={16} weight="fill" /></>}
        </button>
      </SectionCard>
    </form>
  );
}

/* ───────────── Module 1c — Official email verification ───────────── */
function Module1EmailVerify({ app, reload }) {
  const email = app?.kyc?.official_email || "your official email";
  const [cooldown, setCooldown] = useState(60);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const resend = async () => {
    if (cooldown > 0) return;
    setResending(true);
    try {
      await api.post("/ala/email/resend", { app_base_url: window.location.origin });
      toast.success(`Verification link resent to ${email}`);
      setCooldown(60);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    } finally { setResending(false); }
  };

  return (
    <SectionCard icon={<EnvelopeSimple size={18} weight="fill" />} title="Module 1 · Verify your institution email">
      <div className="p-5 rounded-2xl bg-[#F0EFEA] text-center">
        <EnvelopeSimple size={44} weight="duotone" className="text-[#0A3622] mx-auto" />
        <h3 className="font-display text-xl mt-3 mb-1">Check your inbox</h3>
        <p className="text-sm text-[#4F5D4F] max-w-md mx-auto">
          We sent a verification link to <strong className="text-[#0A3622]">{email}</strong>.
          Click the link from your institution inbox to continue your application.
          The link expires in 30 minutes.
        </p>
      </div>

      <div className="mt-5 grid sm:grid-cols-2 gap-3">
        <button
          onClick={resend}
          disabled={cooldown > 0 || resending}
          className="btn-outline justify-center"
          data-testid="ala-email-resend"
        >
          {resending
            ? <CircleNotch className="animate-spin" size={16} />
            : <><ArrowClockwise size={16} weight="bold" /> {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend verification email"}</>}
        </button>
        <button onClick={reload} className="btn-primary justify-center" data-testid="ala-email-refresh">
          <CheckCircle size={16} weight="fill" /> I've verified · refresh status
        </button>
      </div>

      <p className="text-xs text-[#4F5D4F] mt-4">
        Can't find the email? Check your spam folder, or make sure <strong>{email}</strong> accepts mail from <span className="font-mono">noreply@ascentremit.com</span>.
      </p>
    </SectionCard>
  );
}

/* ───────────── Module 2 — Bank linking + underwriting ───────────── */
async function linkBank(acc, reload) {
  try {
    const { data } = await api.post("/ala/bank/link/webhook", { account_id: acc.account_id });
    if (data.state === "offer_available") toast.success("Bank linked — 6-month statement analysed");
    else toast.error(`Not qualified: ${data.state}`);
    reload();
  } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); }
}

function Module2Bank({ reload }) {
  const onLinked = (acc) => linkBank(acc, reload);
  return (
    <SectionCard icon={<Bank size={18} weight="fill" />} title="Module 2 · Link your salary account">
      <p className="text-sm text-[#4F5D4F] mb-5">We pull a 6-month statement to compute your Average Net Monthly Salary and existing debt load. Max monthly installment is capped at 33% of your ANMS.</p>
      <MonoConnectButton label="Link salary account with Mono" testid="ala-link-bank" onLinked={onLinked} />
      <p className="text-xs text-[#4F5D4F] mt-3">We never see or store your bank password. Mono shares a read-only transaction feed with us.</p>
    </SectionCard>
  );
}

/* ───────────── Module 3 — Loan slider + accept ───────────── */
function Module3Loan({ app, reload }) {
  const uw = app?.underwriting || {};
  const offers = uw.offers_by_tenor_days || {};
  const availableTenors = Object.keys(offers).map(Number).sort((a, b) => a - b);
  const [tenor, setTenor] = useState(availableTenors[0] || 30);
  const [principal, setPrincipal] = useState(Math.min(offers[tenor] || 100000, 200000));
  const [terms, setTerms] = useState(null);
  const [busy, setBusy] = useState(false);
  const [signature, setSignature] = useState("");

  const configure = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ala/loan/configure", { principal, tenor_days: tenor });
      setTerms(data.terms);
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const accept = async () => {
    if (signature.trim().length < 2) return toast.error("Type your full name as your e-signature");
    setBusy(true);
    try {
      await api.post("/ala/loan/accept", { e_signature: signature });
      toast.success("Offer accepted — set up Direct Debit mandate");
      reload();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const cap = offers[tenor] || 0;
  return (
    <>
      <div className="grid sm:grid-cols-4 gap-4 mb-6">
        <StatCard label="ANMS" value={fmtNGN(uw.anms)} sub="6-mo average net salary" accent="success" />
        <StatCard label="Existing debt" value={fmtNGN(uw.existing_monthly_debt)} sub="other loan apps" accent="warning" />
        <StatCard label="Max monthly" value={fmtNGN(uw.max_monthly_installment)} sub="33% of ANMS cap" accent="primary" />
        <StatCard label="Salary payslips found" value={uw.salary_count || 0} sub="of 6 months" />
      </div>

      <RateExplanation ratePct={uw.monthly_rate_pct ?? 3} band={uw.risk_band} score={uw.risk_score}
        explanation={uw.rate_explanation} votes={uw.credit_ensemble?.votes} testid="ala-rate" />

      <SectionCard icon={<Sliders size={18} weight="fill" />} title="Module 3 · Configure your loan">
        <div className="flex gap-2 mb-5">
          {availableTenors.map((d) => (
            <button key={d} onClick={() => { setTenor(d); setPrincipal(Math.min(offers[d], principal)); setTerms(null); }}
              className={`flex-1 py-2.5 rounded-full border text-sm font-medium ${tenor === d ? "border-[#0A3622] bg-[#E2F3EB]" : "border-[#E5E3DB] text-[#4F5D4F]"}`}
              data-testid={`ala-tenor-${d}`}>
              {d} days
            </button>
          ))}
        </div>

        <label className="text-xs uppercase tracking-[0.14em] text-[#4F5D4F] font-semibold">Principal · ₦{Number(principal).toLocaleString()} <span className="text-[#4F5D4F]/60">(cap {fmtNGN(cap)})</span></label>
        <input type="range" min={50000} max={cap} step={1000} value={principal}
          onChange={(e) => { setPrincipal(Number(e.target.value)); setTerms(null); }}
          className="w-full accent-[#0A3622] mt-1" data-testid="ala-principal-slider" />

        <button onClick={configure} disabled={busy} className="btn-outline mt-4 text-sm" data-testid="ala-preview-terms">
          {busy ? <CircleNotch className="animate-spin" size={14} /> : "Preview terms"}
        </button>

        {terms && (
          <div className="mt-5 p-5 rounded-2xl bg-[#F0EFEA] grid sm:grid-cols-4 gap-3 text-sm">
            <KV k="Principal" v={fmtNGN(terms.principal)} />
            <KV k="Processing fee (1.5%)" v={fmtNGN(terms.processing_fee)} />
            <KV k={`Interest (${terms.monthly_rate_pct ?? uw.monthly_rate_pct ?? 3}% / mo × ${tenor / 30} mo)`} v={fmtNGN(terms.monthly_interest_total)} />
            <KV k="Total repayment" v={fmtNGN(terms.total_repayment)} tone="primary" />
            <KV k="Installments" v={`${terms.installment_count} × ${fmtNGN(terms.monthly_installment)}`} />
          </div>
        )}

        {terms && (
          <div className="mt-5 border-t border-[#E5E3DB] pt-5">
            <label className="text-xs uppercase tracking-[0.14em] text-[#4F5D4F] font-semibold">Type your full name as e-signature</label>
            <input className="input mt-1" value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="e.g. Ngozi Okafor" data-testid="ala-esig-input" />
            <button onClick={accept} disabled={busy || signature.trim().length < 2} className="btn-primary mt-4" data-testid="ala-accept-offer">
              <CheckCircle size={16} weight="fill" /> Accept & sign
            </button>
          </div>
        )}
      </SectionCard>
    </>
  );
}

/* ───────────── Module 4 — Mandate + disbursement ───────────── */
function Module4Mandate({ app, reload }) {
  const live = app?.mandate_provider === "remita";
  const [busy, setBusy] = useState(false);
  const [ref, setRef] = useState(app?.mandate_reference || null);
  const [mandate, setMandate] = useState(live ? { provider: "remita", activation_url: app?.mandate?.activation_url, otp: app?.mandate?.remita_trans_ref ? { auth_params: app.mandate.auth_params || [] } : null } : null);
  const [otp, setOtp] = useState("");
  const [card, setCard] = useState("");
  const err = (e) => toast.error(formatErr(e?.response?.data?.detail) || e.message);
  const needsCard = (mandate?.otp?.auth_params || []).some((x) => /card/i.test(`${x.name} ${x.label}`));

  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { err(e); } finally { setBusy(false); } };
  const done = (data) => {
    if (data.state === "payout_pending") { toast.success("Mandate active — your advance is queued for payout"); reload(); }
    else toast.info("Remita hasn't marked the mandate active yet. Finish the activation, then check again.");
  };

  const start = () => run(async () => {
    const { data } = await api.post("/ala/mandate/start");
    setRef(data.mandate_reference); setMandate(data);
  });
  const submitOtp = () => run(async () => done((await api.post("/ala/mandate/otp", { otp, card_last4: card || null })).data));
  const check = () => run(async () => done((await api.post("/ala/mandate/check")).data));
  const simulate = (status) => run(async () => {
    const { data } = await api.post("/ala/mandate/webhook", { reference: ref, status });
    if (data.state === "payout_pending") toast.success("Mandate active — your advance is queued for payout");
    else toast.error("Mandate not activated — application closed");
    reload();
  });

  return (
    <SectionCard icon={<ShieldCheck size={18} weight="fill" />} title="Module 4 · Remita Direct Debit mandate">
      <p className="text-sm text-[#4F5D4F] mb-5">Authorise Remita to debit each monthly instalment from your salary account. Once the mandate is active, we send your advance to that account.</p>
      {!ref ? (
        <button onClick={start} disabled={busy} className="btn-primary" data-testid="ala-mandate-start">
          {busy ? <CircleNotch className="animate-spin" size={16} /> : "Set up Remita mandate"}
        </button>
      ) : (
        <div className="space-y-4">
          <div className="p-4 rounded-xl bg-[#F0EFEA] text-sm">
            <div className="text-xs uppercase tracking-[0.14em] text-[#4F5D4F] mb-1">Mandate reference</div>
            <div className="font-mono text-[#0A3622] font-semibold" data-testid="ala-mandate-ref">{ref}</div>
          </div>
          {mandate?.provider === "remita" ? (
            <>
              {mandate.otp && (
                <div className="grid sm:grid-cols-3 gap-3 items-end" data-testid="ala-mandate-otp">
                  <Field label="Code from your bank">
                    <input className="input mt-1 font-mono" maxLength={8} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} data-testid="ala-mandate-otp-input" />
                  </Field>
                  {needsCard && (
                    <Field label="Last 4 digits of your card">
                      <input className="input mt-1 font-mono" maxLength={4} value={card} onChange={(e) => setCard(e.target.value.replace(/\D/g, ""))} data-testid="ala-mandate-card-input" />
                    </Field>
                  )}
                  <button onClick={submitOtp} disabled={busy || otp.length < 4} className="btn-primary justify-center" data-testid="ala-mandate-otp-submit">
                    {busy ? <CircleNotch className="animate-spin" size={14} /> : "Activate mandate"}
                  </button>
                </div>
              )}
              <div className="text-sm text-[#4F5D4F]">
                {mandate.otp ? "No code? " : ""}
                <a href={mandate.activation_url} target="_blank" rel="noreferrer" className="font-semibold text-[#0A3622] underline" data-testid="ala-mandate-activation-link">Activate on Remita's secure page</a>
                {" "}and come back here when you're done.
              </div>
              <button onClick={check} disabled={busy} className="btn-outline" data-testid="ala-mandate-check">
                {busy ? <CircleNotch className="animate-spin" size={14} /> : "I've activated it — check status"}
              </button>
            </>
          ) : (
            <>
              <p className="text-xs text-[#B45309]" data-testid="ala-mandate-mock-note">Sample mode: Remita isn't connected yet, so use the buttons below to simulate the bank's answer.</p>
              <div className="flex gap-3">
                <button onClick={() => simulate("MANDATE_ACTIVE")} disabled={busy} className="btn-primary flex-1" data-testid="ala-mandate-activate">
                  {busy ? <CircleNotch className="animate-spin" size={14} /> : <><CheckCircle size={14} weight="fill" /> Simulate MANDATE_ACTIVE</>}
                </button>
                <button onClick={() => simulate("MANDATE_FAILED")} disabled={busy} className="btn-outline" data-testid="ala-mandate-fail">
                  <XCircle size={14} weight="fill" /> Simulate FAILED
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </SectionCard>
  );
}

/* ───────────── Terminal states ───────────── */
function ModuleDisbursed({ app }) {
  const terms = app.accepted_terms || {};
  const disb = app.disbursement || {};
  return (
    <SectionCard icon={<Lightning size={18} weight="fill" />} title="Loan disbursed">
      <div className="grid sm:grid-cols-3 gap-3 mb-5">
        <KV k="Principal" v={fmtNGN(terms.principal)} tone="primary" />
        <KV k="Monthly installment" v={fmtNGN(terms.monthly_installment)} />
        <KV k="Total repayment" v={fmtNGN(terms.total_repayment)} />
      </div>
      <div className="p-4 bg-[#F0EFEA] rounded-xl text-sm">
        <div className="text-xs uppercase tracking-[0.14em] text-[#4F5D4F] mb-1">NIP Reference</div>
        <div className="font-mono text-[#0A3622] font-semibold">{disb.nip_reference}</div>
        <div className="text-xs text-[#4F5D4F] mt-1">Status: {disb.status}</div>
      </div>
      <h4 className="font-display text-lg mt-6 mb-3">Repayment schedule</h4>
      <table className="w-full text-sm">
        <thead><tr className="text-[#4F5D4F] text-xs uppercase tracking-[0.14em]"><th className="text-left py-2">#</th><th className="text-left py-2">Due date</th><th className="text-right py-2">Amount</th><th className="text-right py-2">Status</th></tr></thead>
        <tbody>{(app.schedule || terms.schedule || []).map((s) => (
          <tr key={s.installment} className="border-t border-[#E5E3DB]" data-testid={`ala-installment-${s.installment}`}><td className="py-2 font-mono">{s.installment}</td><td className="py-2">{s.due_date}</td><td className="py-2 text-right font-mono">{fmtNGN(s.amount)}</td>
            <td className={`py-2 text-right text-xs font-semibold ${s.status === "paid" ? "text-[#2D7A5D]" : "text-[#4F5D4F]"}`}>{s.status === "paid" ? "Collected" : s.status === "debit_submitted" ? "Collecting" : "Upcoming"}</td></tr>
        ))}</tbody>
      </table>
    </SectionCard>
  );
}

function ModuleDeclined({ app, reload }) {
  const restart = async () => {
    if (!window.confirm("Start over? Your previous application will be archived.")) return;
    try {
      await api.post("/ala/otp/start", { phone: app.phone || "+2340000000000" });
      toast.info("Restarted — enter the OTP to continue");
      reload();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); }
  };
  return (
    <SectionCard icon={<XCircle size={18} weight="fill" />} title="Application closed">
      <p className="text-sm text-[#4F5D4F]">{app.declined_reason || "Your Academic Liquidity Advance application was not approved at this time."}</p>
      <button onClick={restart} className="btn-outline mt-5" data-testid="ala-restart">Start a new application</button>
    </SectionCard>
  );
}

/* ───────────── Helpers ───────────── */
function SectionCard({ icon, title, children }) {
  return (
    <div className="card" data-testid={`ala-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[#0A3622]">{icon}</span>
        <h2 className="font-display text-xl font-medium">{title}</h2>
      </div>
      <div className="mt-4">{children}</div>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.14em] text-[#4F5D4F] font-semibold">{label}</span>
      {children}
    </label>
  );
}
function KV({ k, v, tone }) {
  return (
    <div className={`${tone === "primary" ? "text-[#0A3622]" : "text-[#132A13]"}`}>
      <div className="text-[11px] uppercase tracking-[0.12em] text-[#4F5D4F]">{k}</div>
      <div className="font-mono font-semibold mt-0.5">{v}</div>
    </div>
  );
}
